/** Date helpers and grouping logic for Order History collapsible sections. */

export const HISTORY_PERIOD_OPTIONS = [
    { value: 'all', label: 'All History' },
    { value: 'today', label: 'Today' },
    { value: 'this-week', label: 'This Week' },
    { value: 'last-week', label: 'Last Week' },
    { value: 'this-month', label: 'This Month' },
    { value: 'last-month', label: 'Last Month' },
    { value: 'custom', label: 'Custom Date Range' },
];

const startOfDay = (d) => {
    const r = new Date(d);
    r.setHours(0, 0, 0, 0);
    return r;
};

const endOfDay = (d) => {
    const r = new Date(d);
    r.setHours(23, 59, 59, 999);
    return r;
};

const isSameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();

/** US locale: week starts on Sunday. */
const startOfWeek = (d) => {
    const r = startOfDay(d);
    r.setDate(r.getDate() - r.getDay());
    return r;
};

const endOfWeek = (d) => {
    const r = startOfWeek(d);
    r.setDate(r.getDate() + 6);
    return endOfDay(r);
};

const startOfMonth = (d) => {
    const r = startOfDay(d);
    r.setDate(1);
    return r;
};

const endOfMonth = (d) => {
    const r = startOfMonth(d);
    r.setMonth(r.getMonth() + 1);
    r.setDate(0);
    return endOfDay(r);
};

const addDays = (d, n) => {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
};

const formatLongDate = (d) =>
    d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

const formatMonthYear = (d) =>
    d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

/** e.g. "July 20–26, 2026" or "July 27, 2026" when start === end */
const formatDateRange = (start, end) => {
    if (isSameDay(start, end)) {
        return formatLongDate(start);
    }
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    const sameYear = start.getFullYear() === end.getFullYear();
    if (sameMonth) {
        return `${start.toLocaleDateString('en-US', { month: 'long' })} ${start.getDate()}–${end.getDate()}, ${end.getFullYear()}`;
    }
    if (sameYear) {
        const startPart = start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
        const endPart = end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        return `${startPart}–${endPart}`;
    }
    return `${formatLongDate(start)} – ${formatLongDate(end)}`;
};

const parseEntryDate = (entry) => {
    const d = new Date(entry?.changedAt || 0);
    return isNaN(d.getTime()) ? null : d;
};

/** Return { start, end } inclusive bounds for a period preset, relative to `now`. */
export const getPeriodBounds = (period, now = new Date(), customStart = '', customEnd = '') => {
    const today = startOfDay(now);

    switch (period) {
        case 'today':
            return { start: today, end: endOfDay(now) };
        case 'this-week': {
            const start = startOfWeek(today);
            return { start, end: endOfDay(now) };
        }
        case 'last-week': {
            const thisWeekStart = startOfWeek(today);
            const start = addDays(thisWeekStart, -7);
            const end = endOfDay(addDays(thisWeekStart, -1));
            return { start, end };
        }
        case 'this-month': {
            const start = startOfMonth(today);
            return { start, end: endOfDay(now) };
        }
        case 'last-month': {
            const thisMonthStart = startOfMonth(today);
            const end = endOfDay(addDays(thisMonthStart, -1));
            const start = startOfMonth(end);
            return { start, end };
        }
        case 'custom': {
            if (!customStart && !customEnd) return null;
            const start = customStart ? startOfDay(new Date(`${customStart}T00:00:00`)) : null;
            const end = customEnd ? endOfDay(new Date(`${customEnd}T00:00:00`)) : null;
            if (start && isNaN(start.getTime())) return null;
            if (end && isNaN(end.getTime())) return null;
            return {
                start: start || new Date(0),
                end: end || endOfDay(now),
            };
        }
        case 'all':
        default:
            return null;
    }
};

/** Filter history entries by a period preset or custom range. */
export const filterHistoryByPeriod = (entries, period, now = new Date(), customStart = '', customEnd = '') => {
    if (!Array.isArray(entries)) return [];
    const bounds = getPeriodBounds(period, now, customStart, customEnd);
    if (!bounds) return entries;

    const { start, end } = bounds;
    return entries.filter((entry) => {
        const d = parseEntryDate(entry);
        if (!d) return false;
        return d >= start && d <= end;
    });
};

/**
 * Assign a grouping key for a history entry date.
 * Groups: today, yesterday, this-week, last-week, week, month, year.
 */
const getGroupDescriptor = (entryDate, now) => {
    const d = startOfDay(entryDate);
    const today = startOfDay(now);
    const yesterday = addDays(today, -1);
    const thisWeekStart = startOfWeek(today);
    const lastWeekStart = addDays(thisWeekStart, -7);
    const lastWeekEnd = endOfDay(addDays(thisWeekStart, -1));

    if (isSameDay(d, today)) {
        return {
            key: `today-${today.getTime()}`,
            sortKey: today.getTime(),
            title: `Today — ${formatLongDate(today)}`,
            defaultExpanded: true,
        };
    }

    if (isSameDay(d, yesterday)) {
        return {
            key: `yesterday-${yesterday.getTime()}`,
            sortKey: yesterday.getTime(),
            title: `Yesterday — ${formatLongDate(yesterday)}`,
            defaultExpanded: false,
        };
    }

    if (d >= thisWeekStart && d < today) {
        const weekEnd = endOfWeek(today);
        const rangeEnd = weekEnd > today ? yesterday : weekEnd;
        return {
            key: `this-week-${thisWeekStart.getTime()}`,
            sortKey: thisWeekStart.getTime(),
            title: `This Week — ${formatDateRange(thisWeekStart, rangeEnd)}`,
            defaultExpanded: false,
        };
    }

    if (d >= lastWeekStart && d <= lastWeekEnd) {
        return {
            key: `last-week-${lastWeekStart.getTime()}`,
            sortKey: lastWeekStart.getTime(),
            title: `Last Week — ${formatDateRange(lastWeekStart, lastWeekEnd)}`,
            defaultExpanded: false,
        };
    }

    // Recent weeks (within ~8 weeks before last week)
    const eightWeeksAgo = addDays(thisWeekStart, -8 * 7);
    if (d >= eightWeeksAgo) {
        const weekStart = startOfWeek(d);
        const weekEnd = endOfWeek(d);
        return {
            key: `week-${weekStart.getTime()}`,
            sortKey: weekStart.getTime(),
            title: formatDateRange(weekStart, weekEnd),
            defaultExpanded: false,
        };
    }

    // Months within the last ~2 years
    const twoYearsAgo = new Date(today);
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    if (d >= startOfMonth(twoYearsAgo)) {
        const monthStart = startOfMonth(d);
        return {
            key: `month-${monthStart.getFullYear()}-${monthStart.getMonth()}`,
            sortKey: monthStart.getTime(),
            title: formatMonthYear(d),
            defaultExpanded: false,
        };
    }

    const year = d.getFullYear();
    return {
        key: `year-${year}`,
        sortKey: new Date(year, 0, 1).getTime(),
        title: String(year),
        defaultExpanded: false,
    };
};

/**
 * Group filtered history entries into collapsible sections, newest first.
 * Each section: { key, title, entries, defaultExpanded, sortKey }
 */
export const groupHistoryEntries = (entries, now = new Date()) => {
    if (!Array.isArray(entries) || entries.length === 0) return [];

    const sectionMap = new Map();

    entries.forEach((entry) => {
        const entryDate = parseEntryDate(entry);
        if (!entryDate) return;

        const descriptor = getGroupDescriptor(entryDate, now);
        if (!sectionMap.has(descriptor.key)) {
            sectionMap.set(descriptor.key, {
                key: descriptor.key,
                title: descriptor.title,
                sortKey: descriptor.sortKey,
                defaultExpanded: descriptor.defaultExpanded,
                entries: [],
            });
        }
        sectionMap.get(descriptor.key).entries.push(entry);
    });

    const sections = [...sectionMap.values()];

    sections.forEach((section) => {
        section.entries.sort((a, b) => new Date(b.changedAt || 0) - new Date(a.changedAt || 0));
        section.count = section.entries.length;
    });

    sections.sort((a, b) => b.sortKey - a.sortKey);

    return sections;
};

/** Build initial expanded-state map: today expanded; all others collapsed. */
export const getDefaultExpandedState = (sections) => {
    const state = {};
    sections.forEach((section) => {
        state[section.key] = Boolean(section.defaultExpanded);
    });
    return state;
};
